import type { Block, Document, Fragment, Inline, Text } from '@gitbook/slate';
import { Logger } from '@gitbook/util-debug';

const logger = Logger('slate:react');

/**
 * Find the DOM node for a `key`.
 */
export default function findSlateDOMNode(
    key: string | Document | Fragment | Block | Inline | Text,
    win: Window = window
): HTMLElement | null {
    if (typeof key !== 'string') {
        key = key.key;
    }

    const elements = Array.from(win.document.querySelectorAll<HTMLElement>(`[data-key="${key}"]`));

    if (elements.length === 0) {
        logger.warning(
            `Unable to find a DOM node for "${key}". This is often because of forgetting to add \`props.attributes\` to a custom component.`
        );

        return null;
    }

    if (elements.length === 1) {
        // If we have one element, we directly returns it
        return elements[0];
    }

    // If we have multiple elements, we prioritize the one that is in an editable context.
    // This is useful when a fragment is rendered in multiple places, like in a table.
    const editable = elements.find((el) => {
        const contentEditableWrapper = el.closest('[contenteditable]');
        return (
            contentEditableWrapper &&
            contentEditableWrapper.getAttribute('contenteditable') !== 'false'
        );
    });

    return editable || elements[0];
}
